// PayTree client-side configuration.
// Hardcoded because Next.js 16 Turbopack doesn't inline NEXT_PUBLIC_* in client
// bundles (same reason as src/config/paygateto.ts). Rollback path: edit + deploy.

/**
 * Seit 2026-11-04 bietet der Checkout keine PayTree-Zahlart mehr an: PayPal ist
 * als Zahlart entfernt, Karten-/Airwallex-Routen bleiben pausiert. Der volle
 * Katalog, `isPayTreeMethod`, Callback, Status-Poller und Reconcile bleiben,
 * damit bereits gestartete Zahlungen sauber abschliessen.
 * Die bestehenden Merchant-Zugangsdaten und Callback-Wege bleiben bestehen.
 * `?paytree=0` blendet PayTree pro Session aus. Eine Route erst nach Pruefung
 * ihrer Verfuegbarkeit wieder in PAYTREE_ACTIVE_METHOD_IDS aufnehmen, nicht
 * blind zu Stripe zurueckkehren.
 */
const PAYTREE_ENABLED = true;
const PAYTREE_ENABLED_CHANNELS = ["eu", "us"];

/** Namespaced, damit sie nie mit dem PayGate-Katalog kollidieren ("card", "paypal" existieren dort). */
export type PaytreeMethodId = "paytree_paypal" | "paytree_card" | "paytree_klarna";
/** Der `method`-Wert, den die PayTree-API erwartet. */
export type PaytreeApiMethod = "paypal" | "card" | "klarna";

export type PaytreeMethod = {
	id: PaytreeMethodId;
	apiMethod: PaytreeApiMethod;
	labelKey: string;
	iconKey: string;
};

// Reihenfolge = UI-Reihenfolge. PayPal bleibt nur fuer laufende Zahlungsversuche im
// Katalog und wird nicht mehr angeboten (PAYTREE_ACTIVE_METHOD_IDS).
// Der volle `method`-Enum der API ist groesser (card, crypto, wire, paypal, klarna,
// ideal, bancontact, trustly, kakaopay, p24, blik) - hier stehen nur die, die wir
// anbieten oder angeboten haben. Die Texte zu PayPal (Label, Hinweis, Button) sind
// mit dem Angebot entfernt; vor einer Reaktivierung in allen Locales neu anlegen.
export const PAYTREE_METHODS: PaytreeMethod[] = [
	{ id: "paytree_paypal", apiMethod: "paypal", labelKey: "checkout.payment.paypal", iconKey: "paypal" },
	{ id: "paytree_card", apiMethod: "card", labelKey: "checkout.payment.card", iconKey: "card" },
	{
		id: "paytree_klarna",
		apiMethod: "klarna",
		labelKey: "checkout.payment.paytreeAirwallexTitle",
		iconKey: "airwallex",
	},
];

/**
 * Welche Kacheln tatsaechlich angeboten werden - Teilmenge von PAYTREE_METHODS.
 *
 * Leer seit 2026-11-04: PayPal ist als Zahlart entfernt, Karten-/Airwallex-Routen
 * bleiben auch in einem Session-Preview aus, bis ihre Zahlungslinks separat
 * geprueft sind. transaction-initialize lehnt jede Methode ausserhalb dieser
 * Liste vor dem Checkout-Claim ab, also startet auch ein gecachter Checkout keine
 * neue PayTree-Zahlung. `isPayTreeMethod` bleibt bewusst auf dem VOLLEN Katalog,
 * damit ein Intent, der noch mit einer inzwischen deaktivierten Methode unterwegs
 * ist, weiterhin sauber durch Callback und Reconcile laeuft.
 */
const PAYTREE_ACTIVE_METHOD_IDS: readonly PaytreeMethodId[] = [];

/** Amount limits of the former PayPal route (charged amount after credit, channel currency); other methods only need a positive amount. */
export function isPaytreeAmountSupported(method: PaytreeMethodId, amount: number, currency: string): boolean {
	if (!Number.isFinite(amount) || amount <= 0) return false;
	return method !== "paytree_paypal" || (["EUR", "USD"].includes(currency) && amount >= 1 && amount <= 5000);
}

/** Angebotener Katalog (gefiltert), in der Reihenfolge von PAYTREE_METHODS. */
export function getActivePaytreeMethods(
	activeIds: readonly PaytreeMethodId[] = PAYTREE_ACTIVE_METHOD_IDS,
): PaytreeMethod[] {
	return PAYTREE_METHODS.filter((method) => activeIds.includes(method.id));
}

export function isPayTreeMethod(value: unknown): value is PaytreeMethodId {
	return typeof value === "string" && PAYTREE_METHODS.some((method) => method.id === value);
}

export function paytreeApiMethod(id: PaytreeMethodId): PaytreeApiMethod {
	const method = PAYTREE_METHODS.find((m) => m.id === id);
	if (!method) throw new Error(`[PayTree] Unknown method id: ${id}`);
	return method.apiMethod;
}

/**
 * Sichtbare PayTree-Kacheln.
 *
 * `ptParam` ist der `?paytree=`-Query-Parameter und dient dem Prod-Test, solange
 * der Master-Schalter noch aus ist: `?paytree=1` schaltet die Kacheln fuer genau
 * diese eine Session frei, `?paytree=0` ist der Kill-Switch (gewinnt immer).
 * Gleiches Muster wie SellAbroads `?sahc=`. Der Kanal-Gate greift IMMER zuerst -
 * ein Override kann keinen fremden Channel freischalten, und auch `?paytree=1`
 * kann keine Methode zeigen, die nicht in PAYTREE_ACTIVE_METHOD_IDS steht.
 *
 * `enabled` existiert nur fuer Tests; Aufrufer nutzen den Default (Flag).
 */
export function getVisiblePaytreeMethods(
	channel: string,
	ptParam?: string | null,
	enabled: boolean = PAYTREE_ENABLED,
): PaytreeMethod[] {
	if (!PAYTREE_ENABLED_CHANNELS.includes(channel)) return [];
	if (ptParam === "0") return [];
	if (ptParam === "1") return getActivePaytreeMethods();
	return enabled ? getActivePaytreeMethods() : [];
}

export type PaytreeServerConfig = { apiUrl: string; apiToken: string };

/**
 * Normalisiert die konfigurierte Basis-URL auf den Praefix, an den der Client
 * `/v1/...` anhaengt.
 *
 * Hintergrund: PayTree gibt Merchants die VOLLE Endpoint-URL heraus, nicht die
 * Origin - ihr eigenes WooCommerce-Plugin hat als Default-Feldwert
 * `https://api.domain.com/v1/transaction/payment_intent/`. Wer diesen Wert 1:1
 * in PAYTREE_API_URL kopiert, wuerde ohne diese Normalisierung
 * `.../v1/transaction/payment_intent/v1/transaction/payment_intent/` anfragen
 * und bekaeme ein 404, das wie "falscher Cluster" aussieht.
 *
 * Ein etwaiger Pfad-Praefix davor bleibt erhalten (`https://host/paytree/v1/...`
 * -> `https://host/paytree`), Trailing-Slashes fallen weg.
 */
export function normalizePaytreeApiUrl(rawUrl: string): string {
	const trimmed = rawUrl.trim();
	if (!trimmed) return "";
	return trimmed.replace(/\/v1\/transaction\/payment_intent\/?.*$/i, "").replace(/\/+$/, "");
}

/**
 * Server-only Env-Config. Die API-Basis-URL ist PRO MERCHANT/CLUSTER und kommt
 * aus dem PayTree-Back-Office bzw. vom Support (steht bewusst nicht in deren
 * Docs: "Contact us to get the endpoint of your cluster"). Leere Werte = nicht
 * konfiguriert; der Initialize-Branch antwortet dann CHARGE_FAILURE.
 */
export function getPayTreeServerConfig(): PaytreeServerConfig {
	return {
		apiUrl: normalizePaytreeApiUrl(process.env.PAYTREE_API_URL ?? ""),
		apiToken: (process.env.PAYTREE_API_TOKEN ?? "").trim(),
	};
}

export function isPayTreeServerConfigured(config: PaytreeServerConfig = getPayTreeServerConfig()): boolean {
	return Boolean(config.apiUrl && config.apiToken);
}

export type PaytreeReconcileConfig = {
	/** Neueste Kandidatenzeilen pro Lauf: frisch bezahlte Kunden warten JETZT. */
	newestBatch: number;
	/** Rotierende Kandidatenzeilen pro Lauf, aelteste Pruefung zuerst. */
	rotationBatch: number;
	/** Ab wie vielen aufeinanderfolgenden leeren Intents der Ausfall-Alarm feuert. */
	emptyIntentThreshold: number;
	/**
	 * Lesepuffer der Ausfall-Probe. Aus diesen Zeilen wird die
	 * Retry-Supersession gebildet (ein leerer Intent, dessen Checkout ueber
	 * einen zweiten Versuch bezahlt wurde). Geurteilt wird NICHT ueber diese
	 * Breite, sondern ueber `emptyIntentRecencyHours`.
	 */
	emptyIntentWindowHours: number;
	/**
	 * Der Zeitraum, ueber den tatsaechlich geurteilt wird: leere Intents werden
	 * hier gezaehlt, und eine bezahlte Zeile hier legt den Alarm still.
	 *
	 * WARUM NICHT DAS FENSTER. Der Ausfall vom 21.08.2026 dauerte 13,5 Stunden.
	 * Wird die Bedingung "keine einzige Bestellung" ueber 24 Stunden oder ueber
	 * die acht juengsten Zeilen geprueft, muss ein Ausfall erst saemtliche
	 * bezahlten Zeilen aus diesem Bereich schieben - bei Medianzufluss rund 24
	 * bis 30 Stunden. Bis dahin ist der Ausfall vorbei, die bezahlten Zeilen
	 * sind zurueck, und der Waechter haette seinen eigenen Gruendungsvorfall an
	 * fuenf von sieben gemessenen Tagen komplett verpasst.
	 *
	 * WARUM 9 UND NICHT 6. Sechs Stunden waren genau eine Cron-Kadenz, aber zu
	 * eng, um den eigenen Gruendungsvorfall zuverlaessig zu treffen. Der
	 * Zeitraum sammelt wegen der 30-Minuten-Karenz nur 5,5 Stunden Zufluss:
	 * bei Medianzufluss (7 Intents am Tag, Median der gemessenen Woche
	 * 7/7/3/7/15/13/9) liegt die Trefferwahrscheinlichkeit je Lauf bei 22
	 * Prozent und die mittlere Zeit bis zur ersten Mail bei rund 25 Stunden -
	 * fast das Doppelte der 13,5 Stunden, die der Ausfall vom 21.08.2026
	 * gedauert hat. Mit 9 Stunden (8,5 Stunden Zufluss) sind es 45 Prozent und
	 * rund 10 Stunden, also innerhalb der Ausfalldauer.
	 *
	 * WARUM NICHT STATTDESSEN DIE SCHWELLE AUF 2. Ein groesserer Zeitraum
	 * skaliert die leeren UND die bezahlten Zeilen gemeinsam, "keine einzige
	 * Bestellung" wird dadurch eine STAERKERE Aussage. Eine kleinere Schwelle
	 * skaliert nur die leere Seite und senkt die Beweislast auf zwei Abbrueche
	 * ohne Gegenbeleg. Am 09.09.2026 standen 4 Abbrueche neben 5 Bestellungen -
	 * zwei Abbrueche in einer ruhigen Nachtstrecke sind eine haeufige Form und
	 * der kurze Weg zum dritten Fehlalarm.
	 *
	 * Neun Stunden bleiben unter dem 24-Stunden-Lesepuffer, es bewegt sich also
	 * sonst nichts. Ein groesserer Wert als das Fenster waere wirkungslos und
	 * wird in der Probe geklemmt UND geloggt.
	 */
	emptyIntentRecencyHours: number;
	/** Wie lange eine als terminal markierte Zeile aus der Rotation genommen wird. */
	terminalRecheckHours: number;
};

/**
 * Obergrenze fuer JEDEN dieser Werte. PayTrees Rate-Limits sind unbekannt (die
 * Docs nennen keine), deshalb ist ein Tippfehler in der Env-Datei sonst ein
 * Weg, den Cron gegen ein unbekanntes Limit fahren zu lassen.
 */
const PAYTREE_RECONCILE_MAX = 50;

function positiveIntEnv(name: string, fallback: number): number {
	const raw = process.env[name]?.trim();
	if (!raw) return fallback;
	// Bewusst streng: "2.5" und "4abc" sind Konfigurationsfehler, keine 2 bzw. 4.
	if (!/^\d+$/.test(raw)) return fallback;
	const parsed = Number.parseInt(raw, 10);
	if (!Number.isFinite(parsed) || parsed < 1 || parsed > PAYTREE_RECONCILE_MAX) return fallback;
	return parsed;
}

/**
 * Laufzeit-Tuning des PayTree-Reconcile-Passes.
 *
 * `rotationBatch` ist mit Absicht NICHT auf dem alten Wert 4 geblieben. Rechnung
 * dahinter: bei 6h-Kadenz sind 4 Zeilen pro Lauf nur 16 Rotationsplaetze am Tag,
 * und der beobachtete Zufluss liegt bei rund 12 neuen Intents am Tag, am
 * Incident-Tag bei 22. Ein Deckel knapp ueber dem Zufluss laesst die Schlange
 * bei jeder Lastspitze wachsen. 12 pro Lauf sind 48 Plaetze am Tag und decken
 * auch den Incident-Tag mit Reserve. Der Preis sind bis zu 12 statt 4
 * PayTree-Lookups pro Lauf, jeweils mit 3 Sekunden Abstand.
 *
 * ACHTUNG, der Deckel allein garantiert keinen Fortschritt: dafuer sorgt erst
 * die Aufteilung in `splitRotationBatch` (src/lib/paytree/reconcile-selection.ts),
 * den neuen und bereits geprueften Zeilen je eine feste Quote gibt. Ohne sie
 * wuerde jede neue Zeile jede gepruefte verdraengen, egal wie gross der Deckel
 * ist.
 *
 * Die uebrigen Defaults sind der Stand von vor dem 22.08.2026.
 */
export function getPaytreeReconcileConfig(): PaytreeReconcileConfig {
	return {
		newestBatch: positiveIntEnv("PAYTREE_RECONCILE_NEWEST_BATCH", 2),
		rotationBatch: positiveIntEnv("PAYTREE_RECONCILE_ROTATION_BATCH", 12),
		emptyIntentThreshold: positiveIntEnv("PAYTREE_EMPTY_INTENT_THRESHOLD", 3),
		emptyIntentWindowHours: positiveIntEnv("PAYTREE_EMPTY_INTENT_WINDOW_HOURS", 24),
		emptyIntentRecencyHours: positiveIntEnv("PAYTREE_EMPTY_INTENT_RECENCY_HOURS", 9),
		terminalRecheckHours: positiveIntEnv("PAYTREE_TERMINAL_RECHECK_HOURS", 24),
	};
}
